// UltraFlex - Admin PT Management
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, ChevronDown, CheckCircle, XCircle, Clock, AlertTriangle, Eye, Users, Briefcase, FileText, Shield, MapPin, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPTs() {
  const { token } = useAuth();
  const [pts, setPts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [selectedPT, setSelectedPT] = useState(null);
  const [sites, setSites] = useState([]);
  const [ptNotes, setPtNotes] = useState([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteType, setNoteType] = useState('general');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchPTs(); fetchStats(); fetchSites(); }, [search, statusFilter, modelFilter, siteFilter]);

  const fetchPTs = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('query', search);
    if (statusFilter) params.set('status', statusFilter);
    if (modelFilter) params.set('commercial_model', modelFilter);
    if (siteFilter) params.set('site_id', siteFilter);
    try {
      const resp = await fetch(`${API}/pt/list?${params}`, { headers });
      if (resp.ok) { const d = await resp.json(); setPts(d.items || []); }
    } catch {} setLoading(false);
  };

  const fetchStats = async () => {
    try { const r = await fetch(`${API}/pt/stats`, { headers }); if (r.ok) setStats(await r.json()); } catch {}
  };

  const fetchSites = async () => {
    try { const r = await fetch(`${API}/sites`, { headers }); if (r.ok) { const d = await r.json(); setSites(d.items || []); } } catch {}
  };

  const fetchPTDetail = async (id) => {
    try { const r = await fetch(`${API}/pt/${id}`, { headers }); if (r.ok) { const d = await r.json(); setSelectedPT(d); fetchPTNotes(d.id); } } catch {}
  };

  const fetchPTNotes = async (ptId) => {
    try { const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/notes/pt/${ptId}`, { headers }); if (res.ok) setPtNotes(await res.json()); else setPtNotes([]); } catch { setPtNotes([]); }
  };
  const addPTNote = async () => {
    if (!noteContent.trim() || !selectedPT) return;
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/notes/pt/${selectedPT.id}`, { method: 'POST', headers, body: JSON.stringify({ content: noteContent, note_type: noteType }) });
    setNoteContent(''); setNoteType('general'); setShowAddNote(false); fetchPTNotes(selectedPT.id);
  };
  const deletePTNote = async (noteId) => { await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/notes/${noteId}`, { method: 'DELETE', headers }); fetchPTNotes(selectedPT.id); };
  const togglePTNotePin = async (noteId, pinned) => { await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/notes/${noteId}`, { method: 'PATCH', headers, body: JSON.stringify({ is_pinned: !pinned }) }); fetchPTNotes(selectedPT.id); };

  const approvePT = async (id) => {
    await fetch(`${API}/pt/${id}/approve`, { method: 'POST', headers });
    fetchPTs(); fetchStats(); if (selectedPT?.id === id) fetchPTDetail(id);
  };

  const rejectPT = async (id) => {
    const reason = prompt('Rejection reason:');
    if (!reason) return;
    await fetch(`${API}/pt/${id}/reject`, { method: 'POST', headers, body: JSON.stringify({ reason }) });
    fetchPTs(); fetchStats(); if (selectedPT?.id === id) fetchPTDetail(id);
  };

  const suspendPT = async (id) => {
    const reason = prompt('Suspension reason:');
    if (!reason) return;
    await fetch(`${API}/pt/${id}/suspend`, { method: 'POST', headers, body: JSON.stringify({ reason }) });
    fetchPTs(); fetchStats(); if (selectedPT?.id === id) fetchPTDetail(id);
  };

  const reactivatePT = async (id) => {
    await fetch(`${API}/pt/${id}/reactivate`, { method: 'POST', headers });
    fetchPTs(); fetchStats(); if (selectedPT?.id === id) fetchPTDetail(id);
  };

  const setCommercialModel = async (id, model, rate) => {
    const body = { commercial_model: model };
    if (model === 'monthly_rent') body.monthly_rate = parseFloat(rate) || 0;
    else body.session_rate = parseFloat(rate) || 0;
    await fetch(`${API}/pt/${id}/commercial-model`, { method: 'PATCH', headers, body: JSON.stringify(body) });
    fetchPTs(); if (selectedPT?.id === id) fetchPTDetail(id);
  };

  const reviewDoc = async (docId, status) => {
    const notes = status === 'rejected' ? prompt('Rejection notes:') : '';
    await fetch(`${API}/pt/documents/${docId}/review`, { method: 'PATCH', headers, body: JSON.stringify({ status, notes }) });
    if (selectedPT) fetchPTDetail(selectedPT.id);
  };

  const sc = { draft: 'bg-zinc-700 text-zinc-300', submitted: 'bg-amber-500/20 text-amber-400', pending_review: 'bg-amber-500/20 text-amber-400', approved: 'bg-emerald-500/20 text-emerald-400', active: 'bg-emerald-500/20 text-emerald-400', rejected: 'bg-red-500/20 text-red-400', suspended: 'bg-red-500/20 text-red-400', expired: 'bg-orange-500/20 text-orange-400' };

  const deleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    await fetch(`${API}/pt/admin/documents/${docId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchPTDetail(selectedPT.id);
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-pt-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Personal Trainers</h1>
        {stats?.pending_approvals > 0 && <span className="bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-sm font-medium">{stats.pending_approvals} Pending Approval</span>}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Active PTs" value={stats.active_pts} color="text-emerald-400" />
          <StatCard label="Pending" value={stats.pending_approvals} color="text-amber-400" />
          <StatCard label="Suspended" value={stats.suspended_pts} color="text-red-400" />
          <StatCard label="Monthly Rent" value={stats.by_commercial_model?.monthly_rent || 0} color="text-blue-400" />
          <StatCard label="PT Income/mo" value={`£${(stats.monthly_pt_income || 0).toFixed(0)}`} color="text-emerald-400" />
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="admin-pt-search" className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-10 pr-3 py-2.5 text-white text-sm" placeholder="Search PTs..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select data-testid="admin-pt-status-filter" className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {['draft', 'submitted', 'pending_review', 'active', 'rejected', 'suspended', 'expired'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm" value={modelFilter} onChange={e => setModelFilter(e.target.value)}>
          <option value="">All Models</option>
          <option value="ad_hoc">Ad Hoc</option>
          <option value="monthly_rent">Monthly Rent</option>
        </select>
        <select data-testid="admin-pt-site-filter" className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm" value={siteFilter} onChange={e => setSiteFilter(e.target.value)}>
          <option value="">All Sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {/* PT List */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-800 text-zinc-500 text-xs">
            <th className="text-left px-4 py-3">PT</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Model</th><th className="text-left px-4 py-3">Site</th><th className="text-left px-4 py-3">Docs</th><th className="text-right px-4 py-3">Actions</th>
          </tr></thead>
          <tbody>{loading ? <tr><td colSpan={6} className="text-center py-8 text-zinc-500">Loading...</td></tr> : pts.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-zinc-500">No PTs found</td></tr> : pts.map(pt => (
            <tr key={pt.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer" onClick={() => fetchPTDetail(pt.id)} data-testid={`pt-row-${pt.pt_number}`}>
              <td className="px-4 py-3"><p className="text-white font-medium">{pt.first_name} {pt.last_name}</p><p className="text-zinc-500 text-xs">{pt.pt_number} &middot; {pt.email}</p></td>
              <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sc[pt.status] || sc.draft}`}>{pt.status}</span></td>
              <td className="px-4 py-3 text-zinc-400 capitalize">{pt.commercial_model === 'monthly_rent' ? 'Monthly' : 'Ad Hoc'}</td>
              <td className="px-4 py-3 text-zinc-400">{pt.home_site_name || '-'}</td>
              <td className="px-4 py-3 text-zinc-400">{pt.document_count}</td>
              <td className="px-4 py-3 text-right">
                {(pt.status === 'submitted' || pt.status === 'pending_review') && <><button onClick={e => { e.stopPropagation(); approvePT(pt.id); }} className="text-emerald-400 hover:text-emerald-300 text-xs mr-2" data-testid={`approve-pt-${pt.pt_number}`}>Approve</button><button onClick={e => { e.stopPropagation(); rejectPT(pt.id); }} className="text-red-400 hover:text-red-300 text-xs" data-testid={`reject-pt-${pt.pt_number}`}>Reject</button></>}
                {pt.status === 'active' && <button onClick={e => { e.stopPropagation(); suspendPT(pt.id); }} className="text-red-400 hover:text-red-300 text-xs">Suspend</button>}
                {pt.status === 'suspended' && <button onClick={e => { e.stopPropagation(); reactivatePT(pt.id); }} className="text-emerald-400 hover:text-emerald-300 text-xs">Reactivate</button>}
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      {/* PT Detail Modal */}
      <AnimatePresence>{selectedPT && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSelectedPT(null)}>
          <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }} className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="pt-detail-modal">
            <div className="flex items-center justify-between p-5 border-b border-zinc-800">
              <div><h2 className="text-lg font-bold text-white">{selectedPT.first_name} {selectedPT.last_name}</h2><p className="text-zinc-500 text-sm">{selectedPT.pt_number} &middot; {selectedPT.email}</p></div>
              <button onClick={() => setSelectedPT(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <InfoBlock label="Status" value={selectedPT.status} />
                <InfoBlock label="Commercial Model" value={selectedPT.commercial_model === 'monthly_rent' ? `Monthly (£${selectedPT.monthly_rate || 0})` : `Ad Hoc (£${selectedPT.session_rate || 0}/session)`} />
                <InfoBlock label="Home Site" value={selectedPT.home_site_name || '-'} />
                <InfoBlock label="Phone" value={selectedPT.phone} />
                <InfoBlock label="Insurance" value={`${selectedPT.insurance_provider || '-'} (exp: ${selectedPT.insurance_expiry || '-'})`} />
                <InfoBlock label="Billing" value={selectedPT.billing_status || 'N/A'} />
              </div>
              {selectedPT.specialisations?.length > 0 && <div><p className="text-zinc-500 text-xs mb-1">Specialisations</p><div className="flex flex-wrap gap-1">{selectedPT.specialisations.map(s => <span key={s} className="px-2 py-0.5 bg-red-600/20 text-red-400 rounded-full text-xs">{s}</span>)}</div></div>}
              {selectedPT.bio && <div><p className="text-zinc-500 text-xs mb-1">Bio</p><p className="text-zinc-300 text-sm">{selectedPT.bio}</p></div>}

              {/* Documents */}
              <div><p className="text-zinc-500 text-xs mb-2 font-semibold uppercase tracking-wider">Documents ({selectedPT.documents?.length || 0})</p>
                {selectedPT.documents?.map(d => (
                  <div key={d.id} className="flex items-center justify-between bg-zinc-800 rounded-lg p-3 mb-1">
                    <div><p className="text-white text-sm">{d.file_name}</p><p className="text-zinc-500 text-xs capitalize">{d.document_type.replace(/_/g, ' ')} {d.expiry_date ? `\u00B7 Exp: ${d.expiry_date}` : ''}</p></div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${d.review_status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : d.review_status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-zinc-700 text-zinc-400'}`}>{d.review_status}</span>
                      {d.review_status === 'pending' && <>
                        <button onClick={() => reviewDoc(d.id, 'approved')} className="text-emerald-400 text-xs hover:text-emerald-300" data-testid={`approve-doc-${d.id}`}>Approve</button>
                        <button onClick={() => reviewDoc(d.id, 'rejected')} className="text-red-400 text-xs hover:text-red-300">Reject</button>
                      </>}
                      <button onClick={() => deleteDoc(d.id)} className="text-zinc-500 hover:text-red-400 text-xs" data-testid={`delete-doc-${d.id}`} title="Delete document"><XCircle className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <p className="text-zinc-500 text-xs font-semibold uppercase tracking-wider">Internal Notes ({ptNotes.length})</p>
                  <button data-testid="add-pt-note-btn" onClick={() => setShowAddNote(true)} className="text-red-400 text-xs hover:text-red-300 flex items-center gap-1"><FileText className="w-3 h-3" />Add Note</button>
                </div>
                {showAddNote && (
                  <div className="bg-zinc-800 rounded-lg p-3 mb-2 space-y-2">
                    <select value={noteType} onChange={e => setNoteType(e.target.value)} className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-xs w-full">
                      {['general','call','meeting','follow_up','complaint','feedback'].map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
                    </select>
                    <textarea data-testid="pt-note-input" value={noteContent} onChange={e => setNoteContent(e.target.value)} placeholder="Write a note..." rows={2} className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-white text-sm resize-none" />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => { setShowAddNote(false); setNoteContent(''); }} className="px-3 py-1 text-zinc-400 text-xs">Cancel</button>
                      <button data-testid="save-pt-note-btn" onClick={addPTNote} className="px-3 py-1 bg-red-600 text-white rounded text-xs">Save</button>
                    </div>
                  </div>
                )}
                {ptNotes.map(n => (
                  <div key={n.id} className={`rounded-lg p-2.5 mb-1 ${n.is_pinned ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-zinc-800'}`} data-testid={`pt-note-${n.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-0.5 text-[10px]">
                          <span className={`px-1 py-0.5 rounded capitalize ${n.note_type === 'call' ? 'bg-blue-500/20 text-blue-400' : 'bg-zinc-700 text-zinc-400'}`}>{n.note_type?.replace('_',' ')}</span>
                          <span className="text-zinc-500">{n.created_at?.replace('T',' ').slice(0,16)}</span>
                          {n.staff_name && <span className="text-zinc-500">by {n.staff_name}</span>}
                        </div>
                        <p className="text-zinc-300 text-xs">{n.content}</p>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <button onClick={() => togglePTNotePin(n.id, n.is_pinned)} className="text-zinc-500 hover:text-amber-400 p-0.5"><FileText className="w-3 h-3" /></button>
                        <button onClick={() => deletePTNote(n.id)} className="text-zinc-500 hover:text-red-400 p-0.5"><XCircle className="w-3 h-3" /></button>
                      </div>
                    </div>
                  </div>
                ))}
                {!ptNotes.length && !showAddNote && <p className="text-zinc-600 text-xs text-center py-3">No notes yet</p>}
              </div>

              {/* Site Assignment & Commercial Model */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-800">
                <div>
                  <p className="text-zinc-500 text-xs mb-1">Assigned Site</p>
                  <select
                    value={selectedPT.home_site_id || ''}
                    onChange={async (e) => {
                      await fetch(`${API}/pt/${selectedPT.id}`, { method: 'PATCH', headers, body: JSON.stringify({ home_site_id: e.target.value || null }) });
                      fetchPTDetail(selectedPT.id);
                    }}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-white text-sm"
                    data-testid="pt-site-select"
                  >
                    <option value="">All Sites</option>
                    {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-zinc-500 text-xs mb-1">Commercial Model</p>
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm capitalize">{selectedPT.commercial_model === 'monthly_rent' ? 'Monthly' : 'Ad Hoc'}</span>
                    <span className="text-zinc-500 text-sm">£{selectedPT.commercial_model === 'monthly_rent' ? selectedPT.monthly_rate || 0 : selectedPT.session_rate || 0}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-800">
                {(selectedPT.status === 'submitted' || selectedPT.status === 'pending_review') && <><button onClick={() => approvePT(selectedPT.id)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm rounded-lg" data-testid="modal-approve-pt">Approve</button><button onClick={() => rejectPT(selectedPT.id)} className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-sm rounded-lg">Reject</button></>}
                {selectedPT.status === 'active' && <button onClick={() => suspendPT(selectedPT.id)} className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-sm rounded-lg">Suspend</button>}
                {selectedPT.status === 'suspended' && <button onClick={() => reactivatePT(selectedPT.id)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm rounded-lg">Reactivate</button>}
                <button onClick={() => { const model = selectedPT.commercial_model === 'monthly_rent' ? 'ad_hoc' : 'monthly_rent'; const rate = prompt(`Enter ${model === 'monthly_rent' ? 'monthly' : 'session'} rate (£):`); if (rate) setCommercialModel(selectedPT.id, model, rate); }} className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-lg" data-testid="change-model-btn">Change Model</button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}</AnimatePresence>
    </div>
  );
}

const StatCard = ({ label, value, color }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5"><p className="text-zinc-500 text-xs mb-1">{label}</p><p className={`text-xl font-bold ${color}`}>{value}</p></div>
);

const InfoBlock = ({ label, value }) => (
  <div><p className="text-zinc-500 text-xs">{label}</p><p className="text-white text-sm capitalize">{value || '-'}</p></div>
);
